import React from "react";
import { connect } from "react-redux";
import { Col, Row } from "react-bootstrap";
import _, { get } from "lodash";
import PropTypes from "prop-types";
import { FieldArray, Field, formValueSelector, change } from "redux-form";

import { convertSo, validateContact } from "MODULES/SalesOrders/actions";
import { DashedSeparator } from "MODULES/shared/components";
import {
  form,
  Form as AppForm,
  FormInput,
  Check,
  CheckGroup
} from "MODULES/shared/components/Forms";
import {
  AdditionalDetails,
  CustomerSection,
  SellersSection
} from "MODULES/SalesOrders/components/shared/FormSections";
import { ShippingSection } from "MODULES/shared/components/ShippingSection";
import { ON_ORDER_FOR_SALE } from "MODULES/inventories/constants";
import DoNotDoBusinessWarning from "MODULES/companies/shared/components/DoNotDoBusinessWarning";
import { setLeadTime } from "MODULES/shared/helpers";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import {
  shipToOptions as SHIP_TO_OPTIONS,
  TENANT_LOCATION
} from "MODULES/shared/components/ShippingSection/shippingTypes";
import { DISCREET } from "MODULES/SalesOrders/constants";
import FinalDestinationFields from "MODULES/shared/components/FinalDestination/FinalDestinationFields";
import { properCase } from "LIB/formatters/text";
import {
  ACCOUNT_NUMBER,
  CAN_EXCHANGE,
  CARRIER,
  CITY,
  COMPANY,
  COUNTRY,
  DEFAULT_HAZMAT_FEE,
  EXCHANGE_CORE_DUE_DAYS,
  FOB,
  ID,
  LINE1,
  LINE2,
  LINE_ITEMS,
  POSTAL_CODE,
  REGION,
  RETURN_TO,
  RETURN_TO_COMPANY_ADDRESS_ID,
  RETURN_TO_COMPANY_ID,
  RETURN_TO_FOB,
  RETURN_TO_LOCATION_ID,
  RETURN_TO_SHIPPING_ADDRESS,
  RETURN_TO_TYPE,
  SETTINGS,
  SHIP_TO_FOB,
  SHIP_TO_SHIPPING_METHOD,
  SHIP_TO_TYPE,
  SHIPPING_METHOD,
  SHIPPING_TYPE,
  STATE
} from "APP_ROOT/constants";
import { CACHED_SALES_CART, USER } from "MODULES/users/constants";
import { PO_ITEM } from "MODULES/Orders/shared/constants";
import { PURCHASE_ORDER } from "MODULES/PurchaseOrders/constants";
import MultiLocationFeature from "MODULES/shared/components/MultiLocationFeature/MultiLocationFeature";
import ShippingFields from "MODULES/Orders/shared/ShippingFields/ShippingFields";
import { BILLING_ADDRESS, PHYSICAL_ADDRESS } from "MODULES/companies/constants";
import { warrantyForCondition } from "MODULES/OutgoingQuotes/shared/PartDetails";
import { useSettings } from "APP_ROOT/hooks/useCurrentUser";
import DoNotDoBusinessShippingWarning from "MODULES/SalesOrders/components/shared/Warnings/DoNotDoBusinessShippingWarning";
import validate from "./validate";
import {
  CreditLimitWarning,
  NoPmaNoDerWarning,
  TraceTypeWarning,
  WarrantyMinimumWarning,
  BlanketExchangeAgreementWarning,
  DiscreetWarning,
  StoreOwnerWarning
} from "./Warnings";
import ConditionWarning from "../shared/Warnings/ConditionWarning";
import LineItemFields from "./LineItemFields";
import { shouldSetQcPreapproval, transformSO } from "../../helpers";

const initialFOB = lineItem => {
  if (lineItem.has_active_repair) return lineItem.active_repair_ship_to_fob;

  if (
    lineItem.status === ON_ORDER_FOR_SALE &&
    get(lineItem, [PO_ITEM, PURCHASE_ORDER, SHIPPING_TYPE].join(".")) !==
      TENANT_LOCATION
  ) {
    return get(lineItem, [PO_ITEM, PURCHASE_ORDER, FOB].join("."));
  }

  return get(lineItem, "store_location.fob");
};

const FORM_NAME = "convertToSO";

export class Form extends React.Component {
  state = { invalidCompany: false, shipToOptions: SHIP_TO_OPTIONS };

  get shippingAddress() {
    const { shippingToAddress, getField } = this.props;

    const shippingType = getField(SHIP_TO_TYPE);

    if (!shippingToAddress) {
      return null;
    }

    return shippingToAddress[shippingType];
  }

  disableCustomerCqcApproval = (company, lineItem, fieldIndex = null) => {
    if (!company || !lineItem) return null;

    return shouldSetQcPreapproval(company, lineItem, () => {
      if (fieldIndex) {
        this.props.change(
          `${fieldIndex}.customer_qc_preapproval_required`,
          true
        );
      }
    });
  };

  handleCompanySelected = company => {
    this.props.setSelectedCustomer(company);
    this.props.setPreferredShippingMethods(company.shipment_methods);

    if (!company) return null;

    this.props.lineItems.forEach((l, i) => {
      this.disableCustomerCqcApproval(company, l, `line_items[${i}]`);
    });

    if (company && company.shipment_methods) {
      const shipmentMethod = company.shipment_methods[0];
      if (!shipmentMethod) return null;

      [CARRIER, SHIPPING_METHOD, ACCOUNT_NUMBER].forEach(f => {
        this.props.setFieldValue([SHIP_TO_SHIPPING_METHOD, f].join("."))(
          shipmentMethod[f]
        );
      });
    }

    return null;
  };

  render() {
    const {
      formItems = [],
      canExchange,
      getField,
      hasExchange = false,
      lineItems = [],
      selectedSeller,
      setFieldValue,
      selectedCustomer,
      setShippingToAddress
    } = this.props;
    const { invalidCompany, shipToOptions } = this.state;
    const sameStoreOwner =
      [...new Set(lineItems.map(a => a.store_owner_id))].length === 1;

    const formConditions = formItems.map(f => f.condition);
    const itemConditions = lineItems.map(f => f.condition);
    const conditionsMatch = _.isEqual(itemConditions, formConditions);

    const copyBillingAddress = () =>
      [COUNTRY, REGION, STATE, CITY, LINE1, LINE2, POSTAL_CODE].forEach(f => {
        setFieldValue([PHYSICAL_ADDRESS, f].join("."))(
          get(
            selectedCustomer,
            `${BILLING_ADDRESS}.${f}`,
            getField([BILLING_ADDRESS, f].join("."))
          )
        );
      });

    return (
      <AppForm {...this.props} disabled={invalidCompany}>
        <AppForm.Body>
          <DoNotDoBusinessWarning company={selectedCustomer} />
          <MultiLocationFeature>
            {!sameStoreOwner && <StoreOwnerWarning />}
          </MultiLocationFeature>
          <NoPmaNoDerWarning
            company={selectedCustomer}
            inventories={lineItems}
          />
          <TraceTypeWarning
            company={selectedCustomer}
            inventories={lineItems}
          />
          <WarrantyMinimumWarning
            company={selectedCustomer}
            lineItems={formItems}
          />
          <BlanketExchangeAgreementWarning
            company={selectedCustomer}
            exchangeSale={formItems.some(l => l.exchange_sale)}
          />
          <h4>Sellers</h4>
          <SellersSection selectedSeller={selectedSeller} disabled={false} />
          <h4>Customer</h4>
          <CustomerSection
            formName={FORM_NAME}
            company={selectedCustomer}
            onCompanySelected={this.handleCompanySelected}
            setFieldValue={setFieldValue}
            getFieldValue={getField}
            setPrimaryContact={false}
          />
          <DashedSeparator />
          <h4>Shipping</h4>
          <FormRow>
            <Col xs={6}>
              <Field
                name={SHIP_TO_FOB}
                component={FormInput}
                label={"Ship To FOB"}
                required
              />
            </Col>
            <Col xs={6}>
              <CheckGroup justified title={properCase(DISCREET)}>
                <Field
                  name={DISCREET}
                  component={Check}
                  label={properCase(DISCREET)}
                />
              </CheckGroup>
            </Col>
          </FormRow>
          <DiscreetWarning
            discreet={getField(DISCREET)}
            shippingType={getField(SHIP_TO_TYPE)}
          />
          <DoNotDoBusinessShippingWarning />
          <ShippingSection
            copyBillingAddress={copyBillingAddress}
            discreet={getField(DISCREET)}
            getField={getField}
            key={selectedCustomer}
            setFieldValue={setFieldValue}
            shipToCustomer={selectedCustomer}
            shipToOptions={shipToOptions}
            setFOB={setFieldValue(RETURN_TO_FOB)}
            setShippingToAddress={setShippingToAddress}
            beforeShippingMethod={[
              <FinalDestinationFields
                change={this.props.change}
                formName={FORM_NAME}
                shippingToAddress={this.shippingAddress}
                showWarning
              />,
              <DiscreetWarning
                discreet={getField(DISCREET)}
                carrier={getField(`${SHIP_TO_SHIPPING_METHOD}.${CARRIER}`)}
              />
            ]}
            convertSO
            wtDimsReq
            preferredShippingMethods={this.props.preferredShippingMethods}
          />
          <DashedSeparator />
          <AdditionalDetails showDiscreet={false} />
          <DashedSeparator />
          <div>
            <h4>Parts</h4>
            <div
              className="row row-col-gutters-half"
              style={{ marginTop: "26px" }}
            >
              <CreditLimitWarning
                company={selectedCustomer}
                lineItems={formItems}
                customerCreditTerm={getField("credit_terms")}
              />
              <Col md={12}>
                <table className="table table-striped table-numbered margin-bottom-none">
                  <FieldArray
                    name={LINE_ITEMS}
                    component={LineItemFields}
                    canExchange={canExchange}
                    lineItems={lineItems}
                    disableCustomerCqcApproval={this.disableCustomerCqcApproval}
                    selectedCustomer={selectedCustomer}
                    selectedSeller={selectedSeller}
                    change={this.props.change}
                    getField={getField}
                    setFieldValue={setFieldValue}
                    shipToAddress={this.shippingAddress}
                  />
                </table>
                {!conditionsMatch && (
                  <Row>
                    <Col md={12} style={{ marginTop: "20px" }}>
                      <ConditionWarning />
                    </Col>
                  </Row>
                )}
              </Col>
            </div>
          </div>
          {hasExchange && [
            <h4 key="return-to-header">Return To</h4>,
            <Row key="return-to-fob">
              <Col xs={6}>
                <Field
                  name={RETURN_TO_FOB}
                  component={FormInput}
                  label="Return To FOB"
                  required
                />
              </Col>
            </Row>,
            <ShippingFields
              key="return-to-shipping-fields"
              formName={FORM_NAME}
              path={RETURN_TO}
              shippingFields={[
                RETURN_TO_TYPE,
                RETURN_TO_COMPANY_ID,
                RETURN_TO_COMPANY_ADDRESS_ID
              ]}
              renderCarrier={false}
              shippingTypes={[COMPANY]}
              shippingAddressField={RETURN_TO_SHIPPING_ADDRESS}
              required={hasExchange}
              locationFieldName={RETURN_TO_LOCATION_ID}
            />
          ]}
        </AppForm.Body>
      </AppForm>
    );
  }
}

Form.propTypes = {
  formItems: PropTypes.arrayOf(PropTypes.shape({})),
  hasExchange: PropTypes.bool,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      has_active_repair: PropTypes.bool,
      active_repair_ship_to_fob: PropTypes.string
    })
  ),
  returnToType: PropTypes.string,
  selectedSeller: PropTypes.shape({}),
  setFieldValue: PropTypes.func,
  selectedCustomer: PropTypes.instanceOf(Object),
  setSelectedCustomer: PropTypes.func,
  shippingToAddress: PropTypes.instanceOf(Object),
  setShippingToAddress: PropTypes.func
};

const selector = formValueSelector(FORM_NAME);

export default connect(
  (state, props) => {
    const cachedCart = state.session.get(CACHED_SALES_CART)
      ? state.session.get(CACHED_SALES_CART).toJS()
      : {};
    const getCachedItem = item => {
      if (cachedCart && cachedCart.line_items) {
        const cachedItem = cachedCart.line_items.filter(
          l => item.id === l.inventory_id
        )[0];

        return cachedItem || {};
      }

      return {};
    };

    const isFlatExchange = item => {
      const lineItem = _.get(item, PO_ITEM);
      if (!lineItem) return false;

      return lineItem.flat_exchange && lineItem.purchase_for_sale;
    };

    const hazmatFee = get(useSettings(), DEFAULT_HAZMAT_FEE, 75);
    return {
      selectedSeller: get(
        state.session.toJS(),
        "salesCartItems[0].reserve_for_seller"
      ),
      canExchange: state.session.getIn([SETTINGS, CAN_EXCHANGE]),
      hasExchange: (selector(state, LINE_ITEMS) || []).some(
        i => i.exchange_sale
      ),
      formItems: selector(state, LINE_ITEMS),
      returnToType: selector(state, RETURN_TO_TYPE),
      initialValues: {
        company_id: get(cachedCart, "company.id"),
        contact_id: get(cachedCart, "contact.id"),
        credit_terms: cachedCart && cachedCart.customer_credit_terms,
        seller_credit_percent: "25.0",
        seller_id: state.session.getIn([USER, ID]),
        send_sales_order_acknowledgement: true,
        ship_to_type: null,
        ship_to_fob:
          props.lineItems &&
          props.lineItems[0] &&
          initialFOB(props.lineItems[0]),
        line_items: props.lineItems.map(item => ({
          consignment_type: item.consignment_type,
          contingent_on_passing: item.contingent_on_passing,
          lead_time: setLeadTime(item),
          inventory_id: item.id,
          hazmat: item.part.hazmat,
          hazmat_fee: item.part.hazmat ? hazmatFee : null,
          special_container_req: item.part.special_container_req,
          container_fee: item.part.special_container_fee,
          exchange_sale: !!item.brokered_exchange,
          exchange_fee: item.brokered_exchange
            ? _.get(item, "purchase_order_line_item.exchange_srp")
            : undefined,
          outright_value: item.brokered_exchange
            ? _.get(item, "purchase_order_line_item.suggested_retail")
            : undefined,
          flat_exchange: isFlatExchange(item),
          condition: item.condition,
          core_due_days:
            state.session.getIn([SETTINGS, EXCHANGE_CORE_DUE_DAYS]) || 30,
          quantity: item.total_quantity,
          repair_fee: item.brokered_exchange
            ? _.get(
                item,
                "purchase_order_line_item.core_repair_fee_not_to_exceed"
              )
            : undefined,
          warranty: warrantyForCondition(
            item.condition,
            item.part,
            item.tag_date,
            item
          ),
          ...getCachedItem(item)
        }))
      },
      getField: field => selector(state, field)
    };
  },
  dispatch => ({
    setFieldValue: field => val => dispatch(change(FORM_NAME, field, val)),
    onSubmit: data => dispatch(convertSo(transformSO(data)))
  })
)(
  form({
    form: FORM_NAME,
    validate,
    asyncValidate: validateContact,
    asyncBlurFields: ["contact.email", "contact.name", "contact_id"]
  })(Form)
);



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/ConvertFromCartForm/Form.jsx